<template>
  <div class="flex w-full shadow-lg">
    <div
      class="flex flex-col bg-gray-900 text-white"
      :class="appStore.rightSelectedTab === SETTING_PANEL_TABS.TREE_VIEW ? 'border-r border-gray-800' : ''">
      <div class="flex grow flex-col">
        <div
          class="bg-primary flex cursor-pointer items-center justify-center p-4"
          @click="selectTab(SETTING_PANEL_TABS.TREE_VIEW)"
          v-tooltip.top="'Scene Objects'">
          <component :is="PANEL_ICONS['tree']" size="20" stroke-width="1.8" class="text-gray-900" />
        </div>

        <div
          class="flex cursor-pointer items-center justify-center p-4"
          :class="{
            'bg-gray-800': appStore.rightSelectedTab == SETTING_PANEL_TABS.PROJECT_SETTINGS,
          }"
          @click="selectTab(SETTING_PANEL_TABS.PROJECT_SETTINGS)"
          v-tooltip.top="'Project Settings'">
          <component
            :is="PANEL_ICONS['project']"
            size="20"
            stroke-width="1.8"
            class="text-gray-400"
            :class="{
              'text-primary': appStore.rightSelectedTab == SETTING_PANEL_TABS.PROJECT_SETTINGS,
            }" />
        </div>
        <div
          class="flex cursor-pointer items-center justify-center p-4"
          :class="{
            'bg-gray-800': appStore.rightSelectedTab == SETTING_PANEL_TABS.RENDER_SETTINGS,
          }"
          @click="selectTab(SETTING_PANEL_TABS.RENDER_SETTINGS)"
          v-tooltip.top="'Render Settings'">
          <component
            :is="PANEL_ICONS['monitor']"
            size="20"
            stroke-width="1.8"
            class="text-gray-400"
            :class="{
              'text-primary': appStore.rightSelectedTab == SETTING_PANEL_TABS.RENDER_SETTINGS,
            }" />
        </div>

        <div
          class="flex cursor-pointer items-center justify-center p-4"
          :class="{
            'bg-gray-800': appStore.rightSelectedTab == SETTING_PANEL_TABS.REPORTS,
          }"
          @click="selectTab(SETTING_PANEL_TABS.REPORTS)"
          v-tooltip.top="'Reports'">
          <component
            :is="PANEL_ICONS['chart_column']"
            size="20"
            stroke-width="1.8"
            class="text-gray-400"
            :class="{
              'text-primary': appStore.rightSelectedTab == SETTING_PANEL_TABS.REPORTS,
            }" />
        </div>

        <div
          class="cursor-pointer p-4"
          :class="{
            'bg-gray-800': appStore.rightSelectedTab == SETTING_PANEL_TABS.SELECTED_MESH,
          }"
          @click="selectTab(SETTING_PANEL_TABS.SELECTED_MESH)"
          v-tooltip.top="'Selected Mesh Properties'"
          v-if="selectionStore.selectedMeshId">
          <component
            :is="PANEL_ICONS['boxes']"
            size="20"
            stroke-width="1.8"
            class="text-gray-400"
            :class="{
              'text-primary': appStore.rightSelectedTab == SETTING_PANEL_TABS.SELECTED_MESH,
            }" />
        </div>

        <div
          class="cursor-pointer p-4"
          :class="{
            'bg-gray-800': appStore.rightSelectedTab == SETTING_PANEL_TABS.BOOKMARKS,
          }"
          @click="selectTab(SETTING_PANEL_TABS.BOOKMARKS)"
          v-tooltip.top="'Bookmarks'"
          v-if="filteredViewPoints.length > 0">
          <component
            :is="PANEL_ICONS['camera_bookmark']"
            size="20"
            stroke-width="1.8"
            class="text-gray-400"
            :class="{
              'text-primary': appStore.rightSelectedTab == SETTING_PANEL_TABS.BOOKMARKS,
            }" />
        </div>

        <div
          class="cursor-pointer p-4"
          :class="{
            'bg-gray-800': appStore.rightSelectedTab == SETTING_PANEL_TABS.ANNOTATIONS,
          }"
          @click="selectTab(SETTING_PANEL_TABS.ANNOTATIONS)"
          v-tooltip.top="'Annotations'"
          v-if="filteredAnnotations.length > 0">
          <component
            :is="PANEL_ICONS['comment']"
            size="20"
            stroke-width="1.8"
            class="text-gray-400"
            :class="{
              'text-primary': appStore.rightSelectedTab == SETTING_PANEL_TABS.ANNOTATIONS,
            }" />
        </div>

        <div
          class="cursor-pointer p-4"
          :class="{
            'bg-gray-800': appStore.rightSelectedTab == SETTING_PANEL_TABS.MEASUREMENTS,
          }"
          @click="selectTab(SETTING_PANEL_TABS.MEASUREMENTS)"
          v-tooltip.top="'Measurements'"
          v-if="filteredMeasurements.length > 0">
          <component
            :is="PANEL_ICONS['measurement']"
            size="20"
            stroke-width="1.8"
            class="text-gray-400"
            :class="{
              'text-primary': appStore.rightSelectedTab == SETTING_PANEL_TABS.MEASUREMENTS,
            }" />
        </div>
      </div>
      <div class="shrink-0">
        <div class="cursor-pointer px-5 py-4" @click="hidePanel()" v-tooltip.top="hidePanelToolTip">
          <PanelLeftOpen
            size="20"
            stroke-width="1.8"
            class="text-gray-400"
            :class="[collapsedRightPanel ? 'rotate-180' : '']" />
        </div>
      </div>
    </div>

    <div class="flex grow overflow-y-auto text-white">
      <div class="w-full" v-if="appStore.rightSelectedTab == SETTING_PANEL_TABS.TREE_VIEW">
        <TreeView :canvasContainer="canvasContainer" />
      </div>

      <div class="w-full p-4 text-gray-200" v-if="appStore.rightSelectedTab == SETTING_PANEL_TABS.PROJECT_SETTINGS">
        <SettingPanelControls title="Project" nodeType="project_settings" dataType="project_settings" />
      </div>

      <ViewportSettings
        v-if="appStore.rightSelectedTab == SETTING_PANEL_TABS.RENDER_SETTINGS"
        dataType="viewport_settings" />

      <div class="w-full p-4 text-gray-200" v-if="appStore.rightSelectedTab == SETTING_PANEL_TABS.REPORTS">
        <SettingPanelControls title="Reports" nodeType="reports" dataType="viewport_settings" />
      </div>

      <div class="w-full p-4 text-gray-200" v-if="appStore.rightSelectedTab == SETTING_PANEL_TABS.BOOKMARKS">
        <Bookmarks />
      </div>

      <div class="w-full p-4 text-gray-200" v-if="appStore.rightSelectedTab == SETTING_PANEL_TABS.ANNOTATIONS">
        <Annotations />
      </div>

      <div class="w-full p-4 text-gray-200" v-if="appStore.rightSelectedTab == SETTING_PANEL_TABS.MEASUREMENTS">
        <Measurements />
      </div>
    </div>
  </div>
</template>

<script setup>
import eventBus from '@/utils/eventBus';
import { computed, onBeforeUnmount, onMounted } from 'vue';

import Annotations from '@/components/panels/Annotations.vue';
import Bookmarks from '@/components/panels/Bookmarks.vue';
import Measurements from '@/components/panels/Measurements.vue';
import SettingPanelControls from '@/components/SettingPanelControls.vue';
import ViewportSettings from '@/components/ViewportSettings.vue';
import { useAppActionComposable } from '@/composables/useAppActionComposable';
import { PANEL_WIDTH, SETTING_PANEL_TABS } from '@/constants/settingPanels';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useAppStore } from '@/stores/appStore';
import { useSelectionStore } from '@/stores/ui/selection';
import { PanelLeftOpen } from 'lucide-vue-next';
import { watch } from 'vue';
import { PANEL_ICONS } from '../../constants/settingPanels';
import TreeView from './TreeView.vue';

const selectionStore = useSelectionStore();
const cruxModuleStore = useCruxModuleStore();

const appStore = useAppStore();

const props = defineProps({
  canvasContainer: {
    type: Object,
    required: true,
  },
});

const selectedModule = computed(() => {
  if (selectionStore.selectedObjectId) {
    return cruxModuleStore.cruxModules.find((module) => module.code === selectionStore.selectedObjectId);
  }
  return null;
});

const collapsedRightPanel = computed(() => appStore.rightSettingPanelWidth < PANEL_WIDTH.RIGHT_MAX);
const selectTab = (tabName) => {
  appStore.setRightSelectedTab(tabName);
  if (collapsedRightPanel.value) appStore.setRightSettingPanelWidth(PANEL_WIDTH.RIGHT_MAX);
};

const hidePanelToolTip = computed(() => {
  return collapsedRightPanel.value ? 'Show Panel' : 'Hide Panel';
});

const { hidePanel } = useAppActionComposable();

const filteredViewPoints = computed(() => {
  return cruxModuleStore.cruxModules.filter((module) => module.type === 'camera_bookmark') || [];
});

const filteredAnnotations = computed(() => {
  return cruxModuleStore.cruxModules.filter((module) => module.type === 'annotation') || [];
});

const filteredMeasurements = computed(() => {
  return cruxModuleStore.cruxModules.filter((module) => module.type === 'measurement') || [];
});

const handleTabSelection = (tabName) => {
  selectTab(tabName);
};

onMounted(() => {
  eventBus.on('selectRightPanelTab', handleTabSelection);
});

onBeforeUnmount(() => {
  eventBus.off('selectRightPanelTab', handleTabSelection);
});

watch(
  () => selectionStore.selectedObjectId,
  (newVal) => {
    appStore.setTreeParameterPanelOpen(!!newVal);
  }
);
</script>
